import styled from 'styled-components'
import { observer } from 'mobx-react'
import { useCallback, useEffect } from 'react'

import { theme } from 'styles/theme'
import { SvgIcons } from '../Icon/Icon'
import { MenuButton } from '../MenuButton/MenuButton'
import { EmptyUserAvatar, UserAvatar } from '../Users/UserAvatar'
import { Row } from '../Table/Table'

import { NavbarLinks } from './NavbarLinks/NavbarLinks'
import { AboutDialog } from './AboutInfo/AboutInfo' 

import type { ExistingUser } from 'models/Users/UserModel'
import type { OperationStatus } from 'models/common/OperationStatus'
import { logoutLink, type NavbarLink as NavLinkType, type WithAppSectionRoute } from 'router'
import type { WithCommonSettingsStoreStore, WithLoginStore, WithNavBarStore } from 'stores/RootStoreModel'
import { useShowDialog } from 'App'

const Nav = styled.nav`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  color: ${theme.colors.Black};
  background-color: ${theme.colors.navbarBg};
  overflow: visible;
  flex-shrink: 0;
  inline-size: max-content;
  padding: 0.5em;
`

export interface NavbarProps extends WithCommonSettingsStoreStore, WithNavBarStore, WithAppSectionRoute, WithLoginStore {
  linkGroups: ReadonlyArray<ReadonlyArray<NavLinkType>>
}

const NavbarFooter = styled.footer`
  display: flex;
  flex-direction: column;
  gap: 1em;
  margin-block-start: auto;
`

export const Navbar = observer(({ navbarStore, commonSettingsStore, loginStore, currentRoute, linkGroups }: NavbarProps) => {
  useEffect(() => {
    void navbarStore.initializeNavbar()
  }, [navbarStore, loginStore])

  const { name: routeName } = currentRoute

  const linkGroupsWithIsSelected = linkGroups.map((linkGroup) =>
    linkGroup.filter(link => !link.isHidden || !link.isHidden()).map((link) => ({
      ...link,
      isSelected: link.name === routeName || (link.routeNames !== undefined && link.routeNames?.includes(routeName)),
    })),
  )

  const onSignOut = useCallback(() => {
    logoutLink.push()
  }, [])

  return (
    <Nav>
      <NavbarLinks {...{ linkGroups: linkGroupsWithIsSelected }} />
      <NavbarFooter>
        <BottomMenu
          {...{
            user: commonSettingsStore.currentUserAndPermissions?.user_data,
            loadUserInfoStatus: commonSettingsStore.fetchCommonSettingsStatus,
            onSignOut: onSignOut,
            navbarStore,
          }}
        />
      </NavbarFooter>
    </Nav>
  )
})

interface BottomMenuProps extends WithNavBarStore{
  user: ExistingUser | undefined
  loadUserInfoStatus: OperationStatus
  onSignOut: () => void
}

const BottomMenu = observer(({ user, loadUserInfoStatus, onSignOut, navbarStore}: BottomMenuProps ) => {
  const showDialog = useShowDialog()
  const { releaseInfo, fetchStatus } = navbarStore

  // TODO: Center app shouldn't probably show anything before user info (and maybe some other critical data) is loaded.
  const title = loadUserInfoStatus.inProgress
    ? 'Loading user data...'
    : loadUserInfoStatus.error
    ? `Error loading user data: ${loadUserInfoStatus.error.message}`
    : ''

  const userInfoComponent = (
    <Row>
      {user ? <UserAvatar {...{ ...user }} /> : <EmptyUserAvatar {...{ title }} />}
    </Row>
  )
  
  return loadUserInfoStatus.isSuccess && user ? (
    <MenuButton
      {...{
        ButtonComponent: userInfoComponent,
        menuExpansionDirection: { block: 'start', inline: 'start' },
        menuButtonGroups: [
          [{ text: user.name, isDisabled: true, Icon: SvgIcons.UserSvg }],
          [{ text: 'About', onClick: () => {
            showDialog({
              content: <AboutDialog{...{
                fetchStatus: fetchStatus,
                releaseInfo: releaseInfo
              }}/>,
              onConfirmed: () => {},
            }) } , Icon: SvgIcons.AboutSvg }],
          [{ text: 'Sign Out', onClick: () => { void onSignOut() }, Icon: SvgIcons.LogoutSvg }],
        ],
      }}
    />
  ) : (
    userInfoComponent
  )
})

export type NavbarType = typeof Navbar
